import React, { useState } from 'react'
import Select from 'react-select'
import { PlusIcon, XIcon } from '@phosphor-icons/react'
import {
  ExperienceLevel,
  TechnologyList,
  TechStackList,
  YEARS_OF_EXPERIENCE_OPTIONS,
  YEARS_OF_EXPERIENCE_PILLS_DESCRIPTIONS,
  YearsOfExperience,
} from '../../types/TechStacks'

interface MemberTechStackSectionProps {
  technologies: TechnologyList[]
  experienceLevel: ExperienceLevel[]
  selectedTechStackList: TechStackList[]
  onSelectedTechStackListChange: (list: TechStackList[]) => void
}

export const MemberTechStackSection = ({
  technologies,
  experienceLevel,
  selectedTechStackList,
  onSelectedTechStackListChange,
}: MemberTechStackSectionProps) => {
  const [selectedTechnologies, setSelectedTechnologies] = useState(0)
  const [selectedExperience, setSelectedExperience] = useState(0)
  const [selectedYears, setSelectedYears] = useState<YearsOfExperience | ''>(
    '',
  )

  const experienceOptions = () =>
    [...experienceLevel]
      .sort((a, b) => a.description.localeCompare(b.description))
      .map(exp => ({
        label: exp.description,
        value: exp.id.toString(),
      }))

  const yearsOfExperienceOptions = () =>
    YEARS_OF_EXPERIENCE_OPTIONS.map(years => ({
      label: years,
      value: years,
    }))

  const technologiesOptions = () =>
    technologies.map(tech => ({
      label: tech.name,
      value: tech.id.toString(),
    }))

  const addNewTechStackToEmployee = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
    if (
      !selectedTechnologies ||
      !selectedExperience ||
      !selectedYears ||
      selectedTechnologies === 0 ||
      selectedExperience === 0
    ) {
      return
    }

    const techStackWithExperienceExists = selectedTechStackList.find(
      techStack =>
        techStack.idExperienceLevel === selectedExperience &&
        techStack.yearsOfExperience === selectedYears &&
        techStack.idTechnology === selectedTechnologies,
    )

    const techStackExists = selectedTechStackList.find(
      techStack => techStack.idTechnology === selectedTechnologies,
    )

    if (techStackWithExperienceExists) {
      return
    }

    if (techStackExists) {
      onSelectedTechStackListChange(
        selectedTechStackList.map(techStack => {
          if (techStack.idTechnology === selectedTechnologies) {
            return {
              ...techStack,
              idExperienceLevel: selectedExperience,
              yearsOfExperience: selectedYears,
            }
          }
          return techStack
        }),
      )
      return
    }

    onSelectedTechStackListChange([
      ...selectedTechStackList,
      {
        idTechnology: selectedTechnologies,
        idExperienceLevel: selectedExperience,
        yearsOfExperience: selectedYears,
      },
    ])
  }

  return (
    <section className="employee-form__section">
      <div className="employee-form__section-heading">
        <div className="employee-form__section-header">
          <h3 className="employee-form__section-title">Member TechStack</h3>
        </div>
      </div>
      <div className="employee-form__grid employee-form__grid--4 employee-form__grid--align-end">
        <div className="employee-form__field">
          <label
            htmlFor="createEmployeeTechnologyInput"
            className="employee-form__label"
          >
            Technology List
          </label>
          <Select
            className="employee-form__react-select"
            classNamePrefix="select"
            isClearable={true}
            isRtl={false}
            isSearchable={true}
            name="selectedTechnologies"
            placeholder="Select"
            options={technologiesOptions()}
            onChange={option => {
              if (option) {
                setSelectedTechnologies(Number(option?.value))
              } else {
                setSelectedTechnologies(0)
              }
            }}
          />
        </div>
        <div className="employee-form__field">
          <label
            htmlFor="createEmployeeExperienceInput"
            className="employee-form__label"
          >
            Experience Level
          </label>
          <Select
            className="employee-form__react-select"
            classNamePrefix="select"
            isClearable={true}
            isRtl={false}
            isSearchable={true}
            name="selectedExperience"
            placeholder="Select"
            options={experienceOptions()}
            onChange={option => {
              if (option) {
                setSelectedExperience(Number(option?.value))
              } else {
                setSelectedExperience(0)
              }
            }}
          />
        </div>
        <div className="employee-form__field">
          <label
            htmlFor="createEmployeeYearsOfExperienceInput"
            className="employee-form__label"
          >
            Years of Experience
          </label>
          <Select
            className="employee-form__react-select"
            classNamePrefix="select"
            isClearable={true}
            isRtl={false}
            isSearchable={true}
            name="selectedYearsOfExperience"
            placeholder="Select"
            options={yearsOfExperienceOptions()}
            onChange={option => {
              if (option) {
                setSelectedYears(option.value as YearsOfExperience)
              } else {
                setSelectedYears('')
              }
            }}
          />
        </div>
        <button
          type="button"
          className="employee-form__add-btn"
          onClick={addNewTechStackToEmployee}
          aria-label="Add TechStack"
        >
          <PlusIcon size={14} weight="regular" />
          Add TechStack
        </button>
      </div>
      {selectedTechStackList.length > 0 && (
        <div className="employee-form__tech-list">
          <label className="employee-form__label">
            Selected Technology List
          </label>
          <div className="employee-form__chips">
            {selectedTechStackList.map((selectedTechStack, index) => {
              const techName = technologies.find(
                tech => tech.id === selectedTechStack.idTechnology,
              )?.name
              const experienceName = experienceLevel.find(
                exp => exp.id === selectedTechStack.idExperienceLevel,
              )?.description
              const yearsPillDescription = YEARS_OF_EXPERIENCE_PILLS_DESCRIPTIONS.find(
                pill => pill.id === selectedTechStack.yearsOfExperience,
              )?.description
              return (
                <button
                  key={`selectedTechStack-${index}`}
                  type="button"
                  className="employee-form__chip employee-form__chip--selected"
                  onClick={e => {
                    e.preventDefault()
                    onSelectedTechStackListChange(
                      selectedTechStackList.filter((_, i) => i !== index),
                    )
                  }}
                >
                  <span>
                    {techName} · {experienceName}
                    {yearsPillDescription ? ` · ${yearsPillDescription}` : ''}
                  </span>
                  <XIcon size={12} weight="regular" />
                </button>
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}
